"use client";

import { useState, useEffect, useCallback } from "react";

export interface UseLoadingDataResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

/**
 * Shared hook for page-level data loading: runs an async loader once (or when deps change),
 * returns { data, loading, error, reload }. Use on settings, knowledge, welcome, team, etc.
 */
export function useLoadingData<T>(
  loader: () => Promise<T>,
  deps: React.DependencyList = []
): UseLoadingDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loader();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
